/**
 * @fileoverview Mutation hook for the Resend HR Admin Invitation command.
 * @description Resends the pending invitation through `useIdempotentMutation`. Every click needs a
 * new key, and gets one: the key is dropped after a success or a definite refusal (the cooldown, the
 * resend limit), and kept only while the outcome is unknown, so a retry of the same click replays.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { resendClientHrAdminInvite } from '@/features/sales/services/sales-clients.service'

import type { HrAdminInviteResent } from '@/features/sales/types/sales-client.types'

/**
 * @description Resends a client's HR Admin invitation, then refreshes every sales query so the
 * details show the new counters and the next time a resend is allowed.
 * @param {(result: HrAdminInviteResent) => void} [onSuccess] - Called after a success.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the client's id).
 */
export function useResendClientHrAdminInvite(onSuccess?: (result: HrAdminInviteResent) => void) {
  return useIdempotentMutation<string, HrAdminInviteResent>({
    action: 'resend-client-hr-admin-invite',
    /**
     * @description Sends the resend under the key it is given.
     * @param {string} tenantId - The client.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<HrAdminInviteResent>} The invitation's new counters.
     */
    send: (tenantId, key) => resendClientHrAdminInvite(tenantId, key),
    onSuccess,
  })
}
